import React, { useCallback, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DATA_GRID_DEFAULT_PAGE_SIZE } from '../../../../components/DataGridNumberedPagination';
import { DataGrid, GridActionsCellItem, type GridColDef } from '@mui/x-data-grid';
import type { DynamicEntityRow } from '../../../../api/strapiDynamicEntity';
import type { CustomerSampleWrite } from '../../../../api/strapiCustomerSamples';
import { useAuth } from '../../../../auth/AuthContext';
import {
  useCreateCustomerSampleMutation,
  useCustomerSampleLocationsQuery,
  useDeleteCustomerSampleMutation,
  useUpdateCustomerSampleMutation,
} from '../../../../query/hooks';

export type CustomerSamplesLocationsTabScreenProps = {
  customerDocumentId: string;
  readOnly: boolean;
};

type Draft = {
  location_name: string;
  latitudeInput: string;
  longitudeInput: string;
};

function emptyDraft(): Draft {
  return { location_name: '', latitudeInput: '', longitudeInput: '' };
}

function draftFromRow(row: DynamicEntityRow): Draft {
  return {
    location_name: String(row.location_name ?? ''),
    latitudeInput:
      row.latitude != null && Number.isFinite(Number(row.latitude)) ? String(row.latitude) : '',
    longitudeInput:
      row.longitude != null && Number.isFinite(Number(row.longitude)) ? String(row.longitude) : '',
  };
}

function toPayload(draft: Draft): CustomerSampleWrite {
  const latitude = draft.latitudeInput.trim() ? Number(draft.latitudeInput) : undefined;
  const longitude = draft.longitudeInput.trim() ? Number(draft.longitudeInput) : undefined;
  return {
    location_name: draft.location_name.trim(),
    latitude: Number.isFinite(latitude as number) ? latitude : undefined,
    longitude: Number.isFinite(longitude as number) ? longitude : undefined,
  };
}

/** Titik sampel / lokasi untuk pelanggan ini (tab di detail pelanggan). */
export function CustomerSamplesLocationsTabScreen({
  customerDocumentId,
  readOnly,
}: CustomerSamplesLocationsTabScreenProps) {
  const { jwt } = useAuth();
  const listQ = useCustomerSampleLocationsQuery(jwt, customerDocumentId);
  const createMut = useCreateCustomerSampleMutation(jwt, customerDocumentId);
  const updateMut = useUpdateCustomerSampleMutation(jwt, customerDocumentId);
  const deleteMut = useDeleteCustomerSampleMutation(jwt, customerDocumentId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DynamicEntityRow | null>(null);

  const rows = listQ.data ?? [];

  const openCreate = useCallback(() => {
    setEditorMode('create');
    setEditingDocumentId(null);
    setDraft(emptyDraft());
    setFormError(null);
    setEditorOpen(true);
  }, []);

  const openEdit = useCallback((row: DynamicEntityRow) => {
    setEditorMode('edit');
    setEditingDocumentId(row.documentId);
    setDraft(draftFromRow(row));
    setFormError(null);
    setEditorOpen(true);
  }, []);

  async function submitEditor(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    const name = draft.location_name.trim();
    if (!name) {
      setFormError('Nama lokasi wajib diisi.');
      return;
    }
    const payload = toPayload({ ...draft, location_name: name });
    try {
      if (editorMode === 'create') {
        await createMut.mutateAsync(payload);
      } else if (editingDocumentId) {
        await updateMut.mutateAsync({ documentId: editingDocumentId, data: payload });
      }
      setEditorOpen(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Gagal menyimpan.');
    }
  }

  const columns = useMemo((): GridColDef<DynamicEntityRow>[] => {
    const base: GridColDef<DynamicEntityRow>[] = [
      { field: 'location_name', headerName: 'Lokasi', flex: 0.85, minWidth: 160 },
      {
        field: 'latitude',
        headerName: 'Lat',
        flex: 0.35,
        minWidth: 100,
        valueGetter: (_, row) =>
          row.latitude != null ? String(row.latitude) : '',
      },
      {
        field: 'longitude',
        headerName: 'Lon',
        flex: 0.35,
        minWidth: 100,
        valueGetter: (_, row) =>
          row.longitude != null ? String(row.longitude) : '',
      },
      {
        field: 'documentId',
        headerName: 'Id',
        flex: 0.45,
        minWidth: 200,
        valueGetter: (_, row) => row.documentId,
      },
    ];
    if (!readOnly) {
      base.push({
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 88,
        getActions: (params) => [
          <GridActionsCellItem
            key="e"
            icon={<EditOutlinedIcon fontSize="small" />}
            label="Edit"
            onClick={() => openEdit(params.row)}
          />,
          <GridActionsCellItem
            key="d"
            icon={<DeleteOutlineIcon fontSize="small" />}
            label="Delete"
            onClick={() => setPendingDelete(params.row)}
          />,
        ],
      });
    }
    return base;
  }, [readOnly, openEdit]);

  async function confirmDelete() {
    if (!pendingDelete?.documentId) return;
    try {
      await deleteMut.mutateAsync(pendingDelete.documentId);
      deleteMut.reset();
      setPendingDelete(null);
    } catch {
      /* dialog */
    }
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Lokasi atau titik sampling yang terikat pada pelanggan ini. Dipakai saat menghubungkan sampel atau perencanaan
        di lapangan.
      </Typography>
      {listQ.isError && (
        <Alert severity="error">
          {listQ.error instanceof Error ? listQ.error.message : 'Gagal memuat lokasi sampel.'}
        </Alert>
      )}
      {!listQ.isPending && !listQ.isError && rows.length === 0 && (
        <Alert severity="info">
          Belum ada lokasi. {readOnly ? 'Ubah dari mode sunting untuk menambahkan.' : 'Tambahkan baris pertama.'}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {!readOnly && (
          <Tooltip title="Tambah lokasi">
            <IconButton color="primary" onClick={openCreate} aria-label="Tambah lokasi" size="small">
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={listQ.isPending}
        getRowId={(r) => r.documentId || `missing:${Math.random()}`}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: DATA_GRID_DEFAULT_PAGE_SIZE, page: 0 } } }}
        autoHeight
        density="compact"
        sx={{ '& .MuiDataGrid-columnHeaders': { fontWeight: 600 } }}
      />

      <Dialog open={editorOpen} onClose={() => !createMut.isPending && !updateMut.isPending && setEditorOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editorMode === 'create' ? 'Lokasi baru' : 'Ubah lokasi'}</DialogTitle>
        <Box component="form" onSubmit={(ev) => void submitEditor(ev)}>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <TextField
                label="Nama lokasi"
                required
                size="small"
                fullWidth
                value={draft.location_name}
                onChange={(ev) => setDraft((d) => ({ ...d, location_name: ev.target.value }))}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Latitude"
                  type="number"
                  size="small"
                  fullWidth
                  value={draft.latitudeInput}
                  onChange={(ev) => setDraft((d) => ({ ...d, latitudeInput: ev.target.value }))}
                  inputProps={{ step: 'any' }}
                />
                <TextField
                  label="Longitude"
                  type="number"
                  size="small"
                  fullWidth
                  value={draft.longitudeInput}
                  onChange={(ev) => setDraft((d) => ({ ...d, longitudeInput: ev.target.value }))}
                  inputProps={{ step: 'any' }}
                />
              </Stack>
              {formError ? (
                <Typography variant="body2" color="error">
                  {formError}
                </Typography>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button type="button" onClick={() => setEditorOpen(false)}>
              Batal
            </Button>
            <Button type="submit" variant="contained" disabled={createMut.isPending || updateMut.isPending}>
              {createMut.isPending || updateMut.isPending ? 'Menyimpan…' : 'Simpan'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(pendingDelete)} onClose={() => !deleteMut.isPending && setPendingDelete(null)} maxWidth="xs">
        <DialogTitle>Hapus lokasi?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{String(pendingDelete?.location_name ?? pendingDelete?.documentId)}</Typography>
          {deleteMut.isError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {deleteMut.error instanceof Error ? deleteMut.error.message : 'Hapus gagal.'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !deleteMut.isPending && setPendingDelete(null)} disabled={deleteMut.isPending}>
            Batal
          </Button>
          <Button color="error" variant="contained" onClick={() => void confirmDelete()} disabled={deleteMut.isPending}>
            {deleteMut.isPending ? 'Menghapus…' : 'Hapus'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
